/**
 * ProductShowcase — Social Go / Social Pro cover-flow.
 * Each product card can flip between its Introduction and Example video
 * via a segmented toggle just above the phone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Film, Play, Pause, Heart, MessageCircle, Send, Bookmark, MoreHorizontal, RotateCcw, Grid3x3, Clapperboard, UserSquare2, Layers } from 'lucide-react';

const TABS = [
  { key: 'social-go', label: 'Social Go' },
  { key: 'social-pro', label: 'Social Pro' },
];

const _IMG_CDN = 'https://nichecomfiles.co.uk/proposal-examples/defaults';
const SOCIAL_IMAGES = [
  `${_IMG_CDN}/social-image-1.webp?v=2`,
  `${_IMG_CDN}/social-image-2.webp?v=2`,
  `${_IMG_CDN}/social-image-3.webp?v=2`,
  `${_IMG_CDN}/social-image-4.jpg`,
  `${_IMG_CDN}/social-image-5.jpg`,
  `${_IMG_CDN}/social-image-6.jpg`,
  `${_IMG_CDN}/social-image-7.jpg`,
  `${_IMG_CDN}/social-image-8.jpg`,
  `${_IMG_CDN}/social-image-9.jpg`,
];
const IG_AVATAR = `${_IMG_CDN}/gilmours-avatar-2026.png`;
const IG_HIGHLIGHTS = [
  { label: 'Just Listed', img: SOCIAL_IMAGES[0] },
  { label: 'Padstow', img: SOCIAL_IMAGES[3] },
  { label: 'Lifestyle', img: SOCIAL_IMAGES[5] },
  { label: 'Sold', img: SOCIAL_IMAGES[7] },
];

// Product pairs: each leads with its introduction, example swaps in place.
// Each reel type is its own card. Several custom uploads for one slot become a swipe-through set.
export const PRODUCTS = [
  { key: 'sneak-peek', label: 'Sneak Peek', intro: 'intro-social-go', example: 'example-sneak-peek', tiers: ['social-go', 'social-pro'] },
  { key: 'reel-just-listed', label: 'Just Listed Reel', intro: 'intro-reels', example: 'example-reel-just-listed', tiers: ['social-go', 'social-pro'] },
  { key: 'reel-price-reduced', label: 'Price Reduced Reel', example: 'example-reel-price-reduced', tiers: ['social-go', 'social-pro'] },
  { key: 'reel-recently-sold', label: 'Recently Sold Reel', example: 'example-reel-recently-sold', tiers: ['social-go', 'social-pro'] },
  { key: 'property-video', label: 'Property Video', intro: 'intro-social-pro', example: 'example-property-video', tiers: ['social-pro'] },
  { key: 'narratour', label: 'NarraTour', intro: 'intro-narratour', example: 'example-narratour', tiers: ['social-pro'] },
  { key: 'presentour', label: 'PresenTour', intro: 'intro-presentour', example: 'example-presentour', tiers: ['social-pro'] },
  { key: 'social-images', label: 'Social Images', type: 'images', images: SOCIAL_IMAGES, tiers: ['social-go', 'social-pro'] },
  { key: 'post-viewing', label: 'Post Viewing Video', intro: 'intro-feedback', example: 'example-post-viewing', tiers: ['social-pro'] },
  { key: 'post-valuation', label: 'Post Valuation Video', intro: 'intro-post-valuation', example: 'example-post-valuation', tiers: ['social-pro'] },
];

// Every playable video behind a slot: custom alternates when the proposal supplies them, else the default.
export const videosForSlot = (slotMap, id) => {
  const s = slotMap[id];
  if (!s) return [];
  if (s.alternates && s.alternates.length) return s.alternates.map((a) => ({ ...a, label: a.label || s.label }));
  return s.url ? [{ url: s.url, poster_url: s.poster_url || '', label: s.label, is_custom: !!s.is_custom }] : [];
};

// "Reels — Just Listed" on the Reels card reads as "Just Listed"; auto labels ("NarraTour", "NarraTour 2") show nothing
const shortLabel = (label, productLabel) => {
  const s = (label || '').replace(new RegExp(`^${productLabel}\\s*[—–-]\\s*`, 'i'), '').trim();
  return new RegExp(`^${productLabel}\\s*\\d*$`, 'i').test(s) ? '' : s;
};

const InstagramPost = ({ images, interactive, testKey, startIdx = 0, onBack }) => {
  const scrollRef = useRef(null);
  const [idx, setIdx] = useState(startIdx);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.clientWidth) el.scrollTo({ left: startIdx * el.clientWidth });
    setIdx(startIdx);
  }, [startIdx]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el && el.clientWidth) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };

  const go = (e, i) => {
    e.stopPropagation();
    const el = scrollRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(images.length - 1, i));
    el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <div className="ps-ig" data-testid={`ps-images-${testKey}`}>
      <div className="ps-ig-head">
        {onBack && (
          <button className="ps-ig-back" onClick={(e) => { e.stopPropagation(); onBack(); }} aria-label="Back to profile" data-testid="ps-ig-back">
            <ChevronLeft size={18} />
          </button>
        )}
        <img src={IG_AVATAR} alt="Gilmours" className="ps-ig-avatar" />
        <span className="ps-ig-name">gilmours</span>
        <MoreHorizontal size={16} className="ps-ig-more" />
      </div>

      <div className="ps-ig-media">
        <div className="ps-ig-scroll" ref={scrollRef} onScroll={onScroll}>
          {images.map((src, i) => (
            <div key={i} className="ps-ig-slide">
              <img src={src} alt={`Social image ${i + 1}`} loading="lazy" draggable={false} />
            </div>
          ))}
        </div>
        <span className="ps-ig-count">{idx + 1}/{images.length}</span>
        {interactive && images.length > 1 && (
          <>
            <button className="ps-imgs-nav is-prev" onClick={(e) => go(e, idx - 1)} disabled={idx === 0} data-testid="ps-images-prev">
              <ChevronLeft size={15} />
            </button>
            <button className="ps-imgs-nav is-next" onClick={(e) => go(e, idx + 1)} disabled={idx === images.length - 1} data-testid="ps-images-next">
              <ChevronRight size={15} />
            </button>
          </>
        )}
      </div>

      <div className="ps-ig-actions">
        <div className="ps-ig-actions-left">
          <Heart size={20} className="ps-ig-heart" />
          <MessageCircle size={20} />
          <Send size={20} />
        </div>
        <div className="ps-ig-dots">
          {images.map((_, i) => (
            <span key={i} className={`ps-ig-dot ${i === idx ? 'is-active' : ''}`} />
          ))}
        </div>
        <Bookmark size={20} className="ps-ig-save" />
      </div>
      <div className="ps-ig-likes">247 likes</div>
      <div className="ps-ig-caption"><b>gilmours</b> Just listed — a beautifully styled three-bed with a landscaped garden 🌿</div>
      <div className="ps-ig-time">2 hours ago</div>
    </div>
  );
};

/**
 * InstagramAccount — opens on the Gilmours profile (slick 3-up grid of the
 * lifestyle images); tapping a tile opens the post carousel, back returns.
 */
export const InstagramAccount = ({ images, interactive, testKey }) => {
  const [view, setView] = useState('profile');
  const [startIdx, setStartIdx] = useState(0);

  useEffect(() => {
    if (!interactive) setView('profile');
  }, [interactive]);

  if (view === 'post') {
    return <InstagramPost images={images} interactive={interactive} testKey={testKey} startIdx={startIdx} onBack={() => setView('profile')} />;
  }

  const open = (e, i) => {
    e.stopPropagation();
    if (!interactive) return;
    setStartIdx(i);
    setView('post');
  };

  return (
    <div className="ps-ig ps-ig-profile" data-testid={`ps-profile-${testKey}`}>
      <div className="ps-ig-head ps-ig-head-profile">
        <span className="ps-ig-handle">gilmours <span className="ps-ig-verified" aria-hidden="true">✓</span></span>
        <MoreHorizontal size={16} className="ps-ig-more" />
      </div>

      <div className="ps-ig-bio-row">
        <div className="ps-ig-avatar-ring"><img src={IG_AVATAR} alt="Gilmours" className="ps-ig-avatar-lg" /></div>
        <div className="ps-ig-stats">
          <span><b>{images.length * 14}</b>posts</span>
          <span><b>2,431</b>followers</span>
          <span><b>312</b>following</span>
        </div>
      </div>
      <div className="ps-ig-bio">
        <b>Gilmours Estate Agents</b>
        <span>Padstow · Sales · Lettings · Lifestyle 🌊</span>
        <span className="ps-ig-link">gilmours.co.uk</span>
      </div>
      <div className="ps-ig-btns">
        <span className="ps-ig-btn is-primary">Follow</span>
        <span className="ps-ig-btn">Message</span>
        <span className="ps-ig-btn is-icon">▾</span>
      </div>

      <div className="ps-ig-highlights">
        {IG_HIGHLIGHTS.map((h) => (
          <div key={h.label} className="ps-ig-highlight">
            <span className="ps-ig-highlight-ring"><img src={h.img} alt="" loading="lazy" /></span>
            <span>{h.label}</span>
          </div>
        ))}
      </div>

      <div className="ps-ig-tabs">
        <span className="is-active"><Grid3x3 size={15} /></span>
        <span><Clapperboard size={15} /></span>
        <span><UserSquare2 size={15} /></span>
      </div>

      <div className="ps-ig-grid" data-testid="ps-ig-grid">
        {images.map((src, i) => (
          <button
            key={i}
            type="button"
            className="ps-ig-tile"
            onClick={(e) => open(e, i)}
            tabIndex={interactive ? 0 : -1}
            aria-label={`Open post ${i + 1}`}
            data-testid={`ps-ig-tile-${i}`}
          >
            <img src={src} alt={`Lifestyle image ${i + 1}`} loading="lazy" draggable={false} />
            {i % 3 === 0 && <Layers size={12} className="ps-ig-tile-badge" />}
          </button>
        ))}
      </div>
      {interactive && <div className="ps-ig-hint">Tap a post to open it</div>}
    </div>
  );
};

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const easeOutQuad = (p) => 1 - (1 - p) * (1 - p);

// Pre-roll: three value beats before the ring flies in — once per browser session
const PREROLL_KEY = 'nc-showcase-preroll-seen';
const BEAT_MS = [1600, 2000];
const PREROLL_MS = BEAT_MS.reduce((a, b) => a + b, 0);
const prerollSeen = () => { try { return sessionStorage.getItem(PREROLL_KEY) === '1'; } catch { return true; } };
const markPrerollSeen = () => { try { sessionStorage.setItem(PREROLL_KEY, '1'); } catch { /* private mode */ } };

const Preroll = () => {
  let delay = 0;
  const beats = [
    <>One <em>Video</em></>,
    <>An entire <em>content journey</em></>,
  ].map((node, i) => {
    const style = { animationDelay: `${delay}ms`, animationDuration: `${BEAT_MS[i]}ms` };
    delay += BEAT_MS[i];
    return <h2 key={i} className="ps-beat" style={style} data-testid={`ps-preroll-beat-${i + 1}`}>{node}</h2>;
  });
  return (
    <div className="ps-preroll" data-testid="ps-preroll" aria-live="polite">
      {beats}
      <span className="ps-preroll-bar" style={{ animationDuration: `${PREROLL_MS}ms` }} />
    </div>
  );
};

// Roundabout entrance: the ring arrives from far away already spinning, slows like a fairground ride and stops on the first card
const SPIN_MS = 5200;
const SPIN_TURNS = 1;
const SETTLE_FROM = 0.68; // last third: ring geometry eases into the cover-flow layout so the stop needs no second move
const PRE_RING = { scale: 0.58, z: -480, opacity: 0.3 }; // where the faded ring sits behind the pre-roll text
const COLD_START = { scale: 0.2, z: -900, opacity: 0 }; // fly-in start when there was no pre-roll
const smooth = (p) => p * p * (3 - 2 * p);
const lerp = (a, b, k) => a + (b - a) * k;
const cardWidth = () => Math.min(280, window.innerWidth * 0.62);
export const ringRadius = (n) => Math.max(300, (n * (cardWidth() + 70)) / (2 * Math.PI));
// Pre-roll: assets drift in from scattered spots, fade up and gather into a small ring that barely turns (a slow quarter turn, ending aligned with the fly-in)
const PRE_TURN = 90;
const prerollFrame = (u) => ({
  rot: PRE_TURN * (1 - u * u),
  scale: PRE_RING.scale,
  z: PRE_RING.z,
  opacity: PRE_RING.opacity * smooth(Math.min(1, u / 0.3)),
  scatter: 1 - smooth(Math.min(1, u / 0.65)),
  settle: 0,
});
const spinFrame = (t, from) => {
  const fly = easeOutQuad(Math.min(1, t / 0.45));
  return {
    rot: SPIN_TURNS * 360 * (1 - easeOutQuad(t)),
    scale: lerp(from.scale, 1, fly),
    z: from.z * (1 - fly),
    opacity: lerp(from.opacity, 1, Math.min(1, t / 0.1)),
    scatter: 0,
    settle: smooth(Math.max(0, (t - SETTLE_FROM) / (1 - SETTLE_FROM))),
  };
};
// Deterministic "flung out" offset per card for the gather-in
const scatterOf = (idx, radius) => {
  const a = idx * 2.399963;
  return { x: Math.cos(a) * radius * 1.7, y: Math.sin(a * 1.7) * 140, z: -300 - ((idx * 37) % 5) * 110 };
};
// Resting cover-flow pose for a card `offset` places from the centre — mirrors the 'done' inline style exactly
const coverFlowPose = (offset, cardW) => {
  const abs = Math.abs(offset);
  return {
    x: offset * 0.78 * cardW, y: 0, z: 0,
    s: Math.max(1 - abs * 0.13, 0.55),
    tilt: offset === 0 ? 0 : offset > 0 ? -24 : 24,
    opacity: abs > 2 ? 0 : 1,
    bright: abs === 0 ? 1 : Math.max(0.55, 0.8 - abs * 0.12),
    zIndex: 20 - abs,
  };
};
export const ringStyle = (idx, n, frame, radius, cardW) => {
  const theta = (((idx * (360 / n) + frame.rot) % 360) + 360) % 360;
  const signed = theta > 180 ? theta - 360 : theta;
  const rad = (signed * Math.PI) / 180;
  const depth = (Math.cos(rad) + 1) / 2; // 1 front, 0 back
  const sc = frame.scatter ? scatterOf(idx, radius) : null;
  const ring = {
    x: frame.scale * radius * Math.sin(rad) + (sc ? sc.x * frame.scatter : 0),
    y: sc ? sc.y * frame.scatter : 0,
    z: frame.scale * radius * Math.cos(rad) + frame.z - radius + (sc ? sc.z * frame.scatter : 0),
    s: frame.scale,
    tilt: -signed / 2,
    opacity: frame.opacity * (0.55 + 0.45 * depth),
    bright: 0.35 + 0.65 * depth,
    zIndex: Math.round(40 + depth * 40),
  };
  const k = frame.settle;
  const cf = coverFlowPose(idx, cardW);
  const far = cf.opacity === 0; // cards that leave the cover-flow just fade where they are
  const p = k === 0 ? ring : {
    x: far ? ring.x : lerp(ring.x, cf.x, k), y: lerp(ring.y, cf.y, k), z: far ? ring.z : lerp(ring.z, cf.z, k),
    s: far ? ring.s : lerp(ring.s, cf.s, k), tilt: far ? ring.tilt : lerp(ring.tilt, cf.tilt, k),
    opacity: lerp(ring.opacity, cf.opacity, k), bright: far ? ring.bright : lerp(ring.bright, cf.bright, k),
    zIndex: k < 0.5 ? ring.zIndex : cf.zIndex,
  };
  return {
    transform: `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, ${p.z.toFixed(1)}px) scale3d(${p.s.toFixed(4)}, ${p.s.toFixed(4)}, ${p.s.toFixed(4)}) rotateY(${p.tilt.toFixed(2)}deg)`,
    zIndex: p.zIndex,
    opacity: p.opacity.toFixed(3),
    filter: `brightness(${p.bright.toFixed(3)})`,
    pointerEvents: 'none',
  };
};

// preroll: 'always' (homepage) | 'session' (once per browser session) | 'never' (fly-in only)
export const ProductShowcase = ({ videos, preroll = 'session' }) => {
  const [activeTab, setActiveTab] = useState('social-pro');
  const [activeIdx, setActiveIdx] = useState(0);
  const [globalVariant, setGlobalVariant] = useState('intro');
  const [playing, setPlaying] = useState(false);
  const [touch, setTouch] = useState(false); // last input was a finger → never rely on :hover to reveal controls
  const [progress, setProgress] = useState({});
  const [altIdx, setAltIdx] = useState({}); // `${product}-${variant}` → index into that card's video set
  const videoRefs = useRef({});
  // Entrance: 'loading' (waiting for data, ring hidden far away) → 'preroll' (value beats, first time this session) → 'sweep' (ring flies in spinning, stops on the first card) → 'done'
  const [intro, setIntro] = useState(() => (prefersReducedMotion() ? 'done' : 'loading'));
  const cardRefs = useRef({});
  const stageRef = useRef(null);
  const spinRef = useRef({ frame: prerollFrame(0), radius: null });
  const loopRef = useRef(null); // cancel fn of the running entrance loop (pre-roll → fly-in is one continuous timeline)
  const cancelIntro = useCallback(() => setIntro((s) => (s === 'done' ? s : 'done')), []);

  const slotMap = {};
  [...((videos && videos.introductions) || []), ...((videos && videos.examples) || [])].forEach((v) => {
    slotMap[v.id] = v;
  });

  // Proposals with custom (agent-branded) examples open on Examples
  useEffect(() => {
    const anyCustom = ((videos && videos.examples) || []).some((v) => v.is_custom && v.url);
    if (anyCustom) setGlobalVariant('example');
  }, [videos]);

  const items = PRODUCTS
    .filter((p) => p.tiers.includes(activeTab))
    .map((p) => {
      if (p.type === 'images') {
        return { ...p, hasIntro: false, hasExample: false, variant: 'intro', active: null, set: [], setIdx: 0 };
      }
      const introSet = videosForSlot(slotMap, p.intro);
      const exampleSet = (p.examples || [p.example]).flatMap((id) => videosForSlot(slotMap, id));
      const hasIntro = introSet.length > 0;
      const hasExample = exampleSet.length > 0;
      const variant = globalVariant === 'example' && hasExample ? 'example' : (hasIntro ? 'intro' : 'example');
      const set = variant === 'example' ? exampleSet : introSet;
      const setIdx = Math.min(altIdx[`${p.key}-${variant}`] || 0, Math.max(set.length - 1, 0));
      return { ...p, hasIntro, hasExample, variant, set, setIdx, active: set[setIdx] || null };
    })
    // a product with no intro and no example yet (e.g. Reels before its defaults land) stays off the carousel
    .filter((p) => p.type === 'images' || p.hasIntro || p.hasExample);

  const centre = items[activeIdx];

  // Straight into the fly-in once the video data lands; give the first posters a short head start (max 0.5s)
  const firstPosters = items.slice(0, 3).map((i) => i.active?.poster_url || '').join('|');
  const hasData = (((videos && videos.introductions) || []).length + ((videos && videos.examples) || []).length) > 0;
  useEffect(() => {
    if (intro !== 'loading') return undefined;
    let alive = true;
    if (!hasData) {
      const bail = setTimeout(() => { if (alive) setIntro('done'); }, 8000);
      return () => { alive = false; clearTimeout(bail); };
    }
    if (items.length === 0) { setIntro('done'); return undefined; }
    const posters = firstPosters.split('|').filter(Boolean);
    const loaded = Promise.all(posters.map((src) => new Promise((r) => { const im = new Image(); im.onload = r; im.onerror = r; im.src = src; })));
    const cap = new Promise((r) => setTimeout(r, 500));
    Promise.race([loaded, cap]).then(() => {
      if (!alive) return;
      setIntro((s) => {
        if (s !== 'loading') return s;
        if (preroll === 'never' || (preroll !== 'always' && prerollSeen())) return 'sweep';
        markPrerollSeen();
        return 'preroll';
      });
    });
    return () => { alive = false; };
  }, [intro, hasData, items.length, firstPosters, preroll]);

  // Entrance loop: one rAF timeline covering the pre-roll gather (if shown) and the fly-in, written straight to the card nodes
  useEffect(() => {
    const active = intro === 'preroll' || intro === 'sweep';
    if (!active) { if (loopRef.current) { loopRef.current(); loopRef.current = null; } return; }
    if (loopRef.current) return; // preroll → sweep handoff: the running loop carries on
    const n = items.length;
    if (n < 2) { setIntro('done'); return; }
    const keys = items.map((i) => i.key);
    const radius = ringRadius(n);
    const cardW = cardWidth();
    const preMs = intro === 'preroll' ? PREROLL_MS : 0;
    const from = preMs ? PRE_RING : COLD_START;
    const t0 = performance.now();
    let raf;
    let phase = intro;
    const tick = (now) => {
      const el = now - t0;
      let frame;
      let t = 0;
      if (el < preMs) {
        frame = prerollFrame(el / preMs);
      } else {
        t = Math.min(1, (el - preMs) / SPIN_MS);
        frame = spinFrame(t, from);
        if (phase === 'preroll') { phase = 'sweep'; setIntro((s) => (s === 'preroll' ? 'sweep' : s)); }
      }
      spinRef.current = { frame, radius };
      keys.forEach((key, idx) => {
        const node = cardRefs.current[key];
        if (node) Object.assign(node.style, ringStyle(idx, n, frame, radius, cardW));
      });
      if (stageRef.current) stageRef.current.style.perspective = t < 1 ? `${lerp(1000, 1400, frame.settle).toFixed(0)}px` : '';
      if (t < 1) raf = requestAnimationFrame(tick);
      else { loopRef.current = null; setIntro('done'); }
    };
    raf = requestAnimationFrame(tick);
    loopRef.current = () => { cancelAnimationFrame(raf); if (stageRef.current) stageRef.current.style.perspective = ''; };
  }, [intro, items.length]);
  useEffect(() => () => { if (loopRef.current) loopRef.current(); }, []);

  useEffect(() => {
    setActiveIdx(0);
    setPlaying(false);
  }, [activeTab]);

  const pauseAll = () => {
    const prog = {};
    Object.entries(videoRefs.current).forEach(([k, v]) => {
      if (v) {
        if (!v.paused) v.pause();
        prog[k] = v.currentTime > 0.5;
      }
    });
    setProgress((prev) => ({ ...prev, ...prog }));
    setPlaying(false);
  };

  const goTo = (idx) => {
    cancelIntro();
    if (idx < 0 || idx >= items.length) return;
    pauseAll();
    setActiveIdx(idx);
  };

  const setVariant = (variant) => {
    pauseAll();
    setProgress({});
    setGlobalVariant(variant);
  };

  const togglePlay = (item) => {
    const v = videoRefs.current[item.key];
    if (!v) return;
    if (v.paused) {
      v.muted = false;
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setProgress((prev) => ({ ...prev, [item.key]: v.currentTime > 0.5 }));
      setPlaying(false);
    }
  };

  const resume = (item, restart) => {
    const v = videoRefs.current[item.key];
    if (!v) return;
    if (restart) v.currentTime = 0;
    v.muted = false;
    v.play().then(() => setPlaying(true)).catch(() => {});
  };

  // Step through a card's video set (e.g. Reels: Just Listed → Price Reduced → Recently Sold)
  const stepAlt = (item, dir) => {
    if (!item.set || item.set.length < 2) return;
    pauseAll();
    setProgress((prev) => ({ ...prev, [item.key]: false }));
    const next = (item.setIdx + dir + item.set.length) % item.set.length;
    setAltIdx((prev) => ({ ...prev, [`${item.key}-${item.variant}`]: next }));
  };

  return (
    <div className={`ps ${touch ? 'ps-touch' : ''} ps-intro-${intro}`} data-testid="product-showcase" data-intro={intro} onPointerDown={(e) => { setTouch(e.pointerType === 'touch'); cancelIntro(); }}>
      {/* Tabs */}
      <div className="ps-tabs" data-testid="ps-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`ps-tab ${activeTab === tab.key ? 'is-active' : ''} ${tab.disabled ? 'is-disabled' : ''}`}
            onClick={() => { if (!tab.disabled) { cancelIntro(); setActiveTab(tab.key); } }}
            disabled={tab.disabled}
            data-testid={`ps-tab-${tab.key}`}
          >
            {tab.label}
            {tab.disabled && <span className="ps-tab-soon">Coming soon</span>}
          </button>
        ))}
      </div>

      {/* Cover-flow */}
      <div className="ps-stage" data-testid="ps-stage" ref={stageRef}>
        {intro === 'preroll' && <Preroll />}
        {items.length === 0 && intro !== 'loading' && (
          <div className="ps-empty">
            <Film size={28} />
            <span>Examples coming soon</span>
          </div>
        )}

        {items.map((item, idx) => {
          const offset = idx - activeIdx;
          const abs = Math.abs(offset);
          const isCenter = offset === 0;
          const media = item.active;
          const style = intro !== 'done'
            ? ringStyle(idx, items.length, spinRef.current.frame, spinRef.current.radius ?? ringRadius(items.length), cardWidth())
            : {
              transform: `translateX(${offset * 78}%) scale(${Math.max(1 - abs * 0.13, 0.55)}) rotateY(${offset === 0 ? 0 : offset > 0 ? -24 : 24}deg)`,
              zIndex: 20 - abs,
              opacity: abs > 2 ? 0 : 1,
              filter: isCenter ? 'brightness(1)' : `brightness(${Math.max(0.55, 0.8 - abs * 0.12)})`,
              pointerEvents: abs > 2 ? 'none' : 'auto',
            };
          return (
            <div
              key={item.key}
              ref={(el) => { cardRefs.current[item.key] = el; }}
              className={`ps-card ${isCenter ? 'is-center' : ''} ${isCenter && playing ? 'is-playing' : ''}`}
              style={style}
              onClick={() => (isCenter ? (media?.url && togglePlay(item)) : goTo(idx))}
              data-testid={`ps-card-${item.key}`}
            >
              {item.type === 'images' ? (
                <InstagramAccount images={item.images} interactive={isCenter} testKey={item.key} />
              ) : media?.url ? (
                <video
                  key={`${item.key}-${item.variant}-${item.setIdx}`}
                  ref={(el) => { videoRefs.current[item.key] = el; }}
                  src={media.url}
                  poster={media.poster_url || undefined}
                  className="ps-video"
                  preload="metadata"
                  muted
                  loop
                  playsInline
                  onEnded={() => setPlaying(false)}
                />
              ) : (
                <div className="ps-placeholder">
                  <Film size={26} />
                  <span>{item.variant === 'example' ? 'Example coming soon' : 'Video coming soon'}</span>
                </div>
              )}

              {item.set && item.set.length > 1 && !isCenter && (
                <span className="ps-multi-badge" data-testid={`ps-multi-badge-${item.key}`}><Layers size={11} /> {item.set.length} demos</span>
              )}

              {isCenter && item.set && item.set.length > 1 && (
                <div className="ps-alt" data-testid={`ps-alt-${item.key}`} onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="ps-alt-btn" onClick={() => stepAlt(item, -1)} aria-label="Previous demo" data-testid="ps-alt-prev">
                    <ChevronLeft size={16} />
                  </button>
                  <span className="ps-alt-label" data-testid="ps-alt-count">
                    <span className="ps-alt-kicker">Demo {item.setIdx + 1} of {item.set.length}</span>
                    {shortLabel(media?.label, item.label) && <span className="ps-alt-name">{shortLabel(media.label, item.label)}</span>}
                  </span>
                  <button type="button" className="ps-alt-btn is-next" onClick={() => stepAlt(item, 1)} aria-label="Next demo" data-testid="ps-alt-next">
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {isCenter && media?.url && (
                playing ? (
                  <button className="ps-play" data-testid="ps-play-btn" onClick={(e) => { e.stopPropagation(); togglePlay(item); }}>
                    <Pause size={22} />
                  </button>
                ) : progress[item.key] ? (
                  <div className="ps-resume">
                    <button className="ps-resume-btn" data-testid="ps-continue-btn" onClick={(e) => { e.stopPropagation(); resume(item, false); }}>
                      <Play size={13} /> Continue
                    </button>
                    <button className="ps-resume-btn" data-testid="ps-restart-btn" onClick={(e) => { e.stopPropagation(); resume(item, true); }}>
                      <RotateCcw size={13} /> Restart
                    </button>
                  </div>
                ) : (
                  <button className="ps-play" data-testid="ps-play-btn" onClick={(e) => { e.stopPropagation(); togglePlay(item); }}>
                    <Play size={22} style={{ marginLeft: 3 }} />
                  </button>
                )
              )}
            </div>
          );
        })}

        {items.length > 1 && (
          <>
            <button
              className="ps-arrow ps-arrow-left"
              onClick={() => goTo(activeIdx - 1)}
              disabled={activeIdx === 0}
              data-testid="ps-arrow-left"
            >
              <ChevronLeft size={26} />
            </button>
            <button
              className="ps-arrow ps-arrow-right"
              onClick={() => goTo(activeIdx + 1)}
              disabled={activeIdx === items.length - 1}
              data-testid="ps-arrow-right"
            >
              <ChevronRight size={26} />
            </button>
          </>
        )}
      </div>

      {/* Product title + Intro / Example toggle — below the preview */}
      <div className="ps-variant-bar" data-testid="ps-variant-bar">
        {centre && (
          <>
            <div className="ps-topbar-title" data-testid={`ps-label-${centre.key}`}>{centre.label}</div>
            {(centre.hasIntro || centre.hasExample) && (
              <div className="ps-variant" data-testid={`ps-variant-${centre.key}`}>
                <button
                  className={`ps-variant-btn ${globalVariant === 'intro' ? 'is-active' : ''}`}
                  onClick={() => setVariant('intro')}
                  data-testid={`ps-variant-intro-${centre.key}`}
                >
                  Intros
                </button>
                <button
                  className={`ps-variant-btn ${globalVariant === 'example' ? 'is-active' : ''}`}
                  onClick={() => setVariant('example')}
                  data-testid={`ps-variant-example-${centre.key}`}
                >
                  Examples
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Dots */}
      {items.length > 1 && (
        <div className="ps-dots" data-testid="ps-dots">
          {items.map((item, idx) => (
            <button
              key={item.key}
              className={`ps-dot ${idx === activeIdx ? 'is-active' : ''}`}
              onClick={() => goTo(idx)}
              aria-label={item.label}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductShowcase;
